/**
 * Faz a tecla TAB inserir um caractere de tabulação dentro de qualquer
 * <textarea> em vez de mover o foco pro próximo campo.
 *
 * Why: usuários (especialmente médicos digitando observações longas)
 * pediram poder indentar livremente sem perder o foco.
 *
 * How to apply: Shift+TAB continua escapando do textarea pra preservar
 * navegação por teclado. Textarea com data-no-tab-indent="true" também
 * mantém o comportamento nativo (escape hatch caso algum campo específico
 * precise voltar ao default).
 */
let installed = false;

export function installTextareaTabIndent(): void {
  // Evita registrar o listener duas vezes (ex: HMR re-executando main.tsx,
  // o que faria inserir múltiplos tabs por tecla).
  if (installed) return;
  installed = true;

  // Fallback p/ navegadores sem execCommand: React intercepta input via setter
  // próprio, então chamamos o setter nativo e disparamos um evento input manual.
  const nativeValueSetter = Object.getOwnPropertyDescriptor(
    HTMLTextAreaElement.prototype,
    'value',
  )?.set;

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Tab') return;
    if (e.shiftKey || e.ctrlKey || e.metaKey || e.altKey) return;

    const target = e.target;
    if (!(target instanceof HTMLTextAreaElement)) return;
    if (target.disabled || target.readOnly) return;
    if (target.dataset.noTabIndent === 'true') return;

    e.preventDefault();

    // execCommand('insertText') preserva o histórico de undo/redo nativo do
    // textarea (Ctrl+Z) e dispara o onChange do React sozinho. É deprecated,
    // mas é a única forma de inserir mantendo o undo, e os navegadores atuais
    // suportam. Só caímos no método manual (que zera o undo) se ele falhar.
    if (document.execCommand('insertText', false, '\t')) return;

    const { selectionStart, selectionEnd, value } = target;
    const TAB = '\t';
    const newValue = value.slice(0, selectionStart) + TAB + value.slice(selectionEnd);
    const newCursor = selectionStart + TAB.length;

    if (nativeValueSetter) {
      nativeValueSetter.call(target, newValue);
    } else {
      target.value = newValue;
    }
    target.setSelectionRange(newCursor, newCursor);
    target.dispatchEvent(new Event('input', { bubbles: true }));
  });
}
